import { makeApi } from "@zodios/core";
import { z } from "zod";

import { supportSubscriberNeedCodeSchema } from "@/domains/support/support-subscriber-need";
import { branded } from "@/lib/helpers/branded-helper";
import { DATE_REGEX } from "@/lib/regex.ts";
import { appointmentCancellationReasonDtoSchema } from "@/types/backoffice/dto/appointment/appointment-cancellation-reason-dto.ts";
import { appointmentDtoSchema } from "@/types/backoffice/dto/appointment/appointment-dto.ts";
import { appointmentManualBookingDtoSchema } from "@/types/backoffice/dto/appointment/appointment-manual-booking-dto.ts";
import {
  appointmentSearchDtoSchema,
  EAppointmentSlotState,
} from "@/types/backoffice/dto/appointment/appointment-search-dto.ts";
import { EAppointmentType } from "@/types/backoffice/dto/appointment/appointment-type-dto.ts";
import { serviceProviderDtoSchema } from "@/types/backoffice/dto/appointment/service-provider-dto.ts";
import { eventDtoSchema, eventTreeSchema } from "@/types/backoffice/dto/event/event-dto.ts";
import { lastButtonEventDtoSchema } from "@/types/backoffice/dto/event/last-button-event-dto.ts";
import { modulatorDtoSchema } from "@/types/backoffice/dto/modulator/modulator-dto.ts";
import { modulatorPowerMeasureHistoryDto } from "@/types/backoffice/dto/modulator/modulator-power-measure-dto";
import { momboxDtoSchema } from "@/types/backoffice/dto/mombox/mombox-dto.ts";
import { momboxLastConnectionDateDtoSchema } from "@/types/backoffice/dto/mombox/mombox-last-connection-date-dto.ts";
import { sitePhotoDtoSchema } from "@/types/backoffice/dto/photo/site-photo-dto.ts";
import { sensorRequestDtoSchema } from "@/types/backoffice/dto/sensor/sensor-request-dto.ts";
import { patchSiteDocumentMetadataDtoSchema } from "@/types/backoffice/dto/site-documents/patch-site-document-metadata-dto.ts";
import { siteDocumentsDtoSchema } from "@/types/backoffice/dto/site-documents/site-document-dto";
import { siteDocumentsWithPhotosDtoSchema } from "@/types/backoffice/dto/site-documents/site-document-with-photo-dto";
import { siteAlertDtoSchema } from "@/types/backoffice/dto/site/site-alert-dto.ts";
import { siteAppointmentPreparationDtoSchema } from "@/types/backoffice/dto/site/site-appointment-preparation-dto.ts";
import { siteConstraintsSummaryDtoSchema } from "@/types/backoffice/dto/site/site-constraints-summary-dto.ts";
import { siteDsoConsentsDtoSchema } from "@/types/backoffice/dto/site/site-dso-consents-dto.ts";
import { siteDsoStatusDtoSchema } from "@/types/backoffice/dto/site/site-dso-status-dto.ts";
import { geocodedAddressDtoSchema, postalAddressDtoSchema } from "@/types/backoffice/dto/site/site-dto.ts";
import {
  siteInstallationFlowDtoSchema,
  siteInstallationStatusDtoSchema,
} from "@/types/backoffice/dto/site/site-installation-flow-dto.ts";
import { sitePartnerAddressDtoSchema } from "@/types/backoffice/dto/site/site-partner-address-dto";
import { siteWorkingConditionDtoSchema } from "@/types/backoffice/dto/site/working-condition/site-working-condition-dto.ts";

export const siteSearchSchema = z.object({
  modulatorSearchInfo: z.object({
    csId: z.number().nullable(),
    hwId: z.number().nullable(),
    neuronId: z.string().nullable(),
    serialNumber: z.string().nullable(),
  }),
  pilotSearchInfo: z.object({
    csId: z.number().nullable(),
    hwId: z.number().nullable(),
  }),
  siteSearchInfo: z.object({
    deliveryPoint: z.string().nullable(),
    externId: z.string().nullable(),
    id: z.number(),
    postalAddress: postalAddressDtoSchema.nullable(),
    geocodedAddress: geocodedAddressDtoSchema.nullable(),
  }),
  subscriberSearchInfo: z
    .object({
      companyName: z.string().nullable(),
      emailList: z.array(z.any()),
      firstname: z.string().nullable(),
      lastname: z.string().nullable(),
      id: z.number().nullable(),
      phoneList: z.array(z.any()),
    })
    .nullable(),
});

export type TSiteSearchSchema = z.infer<typeof siteSearchSchema>;

export const findSiteByFiltersBody = z.array(
  z.object({
    value: z.string().min(1),
    field: z.enum([
      /* ------------- SUBSCRIBER ------------- */
      "subscriberId",
      "subscriberLastname",
      "subscriberFirstname",
      "email",
      "phone",
      "companyName",
      /* ------------- SITE ------------- */
      "siteId",
      "externId",
      "street",
      "postal",
      "town",
      "country",
      "deliveryPoint",
      /* ------------- PILOT ------------- */
      "pilotHwId",
      "pilotCsId",
      /* ------------- MODULATOR ------------- */
      "modSerial",
      "modHwId",
      "modCsId",
      "modNeuronId",
    ]),
    filterType: z.enum(["EQUALS", "CONTAINS", "STARTS_BY", "ENDS_BY"]),
  }),
);

export const sendSupportMailBody = z.object({
  comments: z.string(),
  supportTypeId: z.number(),
  supportSubscriberNeedCode: supportSubscriberNeedCodeSchema.nullish(),
});
export const sendRotationMailBody = z.object({
  comments: z.string(),
  detachSubscriber: z.boolean(),
});

export const backofficeSiteApi = makeApi([
  {
    method: "get",
    path: "/:id/alert",
    alias: "findSiteAlertsById",
    response: z.array(siteAlertDtoSchema),
    parameters: [
      {
        type: "Path",
        name: "id",
        schema: z.number(),
      },
    ],
  },
  {
    method: "get",
    path: "/:id/address/partner",
    alias: "findSitePartnerAddress",
    response: z.array(sitePartnerAddressDtoSchema),
  },
  {
    method: "put",
    path: "/:id/address/partner",
    alias: "updateSitePartnerAddress",
    response: z.any(),
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: sitePartnerAddressDtoSchema,
      },
    ],
  },
  {
    method: "put",
    path: "/:id/eligible-appliance",
    alias: "updateSiteEligibleApplianceNumber",
    response: z.any(),
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: z.object({ eligibleApplianceNumber: z.number().min(0) }),
      },
    ],
  },
  {
    method: "post",
    path: "/search",
    alias: "findSiteByFilters",
    response: z.array(siteSearchSchema),
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: findSiteByFiltersBody,
      },
    ],
  },
  {
    method: "get",
    path: "/:id/photosList",
    alias: "findSitePhotos",
    response: z.array(sitePhotoDtoSchema),
  },
  {
    method: "get",
    path: "/:id/modulator/working-condition",
    alias: "findSiteWorkingCondition",
    response: z.array(siteWorkingConditionDtoSchema),
  },
  {
    method: "get",
    path: "/:id/dsoConsents",
    alias: "findSiteDsoConsents",
    response: siteDsoConsentsDtoSchema,
  },
  {
    method: "get",
    path: "/:id/dsoStatus",
    alias: "findSiteDsoStatus",
    response: siteDsoStatusDtoSchema,
  },
  {
    method: "get",
    path: "/:id/last-button-event",
    alias: "findSiteLastButtonEvents",
    response: z.array(lastButtonEventDtoSchema),
  },
  {
    method: "get",
    path: "/:id/constraints-summary",
    alias: "findSiteConstraintsSummary",
    response: siteConstraintsSummaryDtoSchema,
  },
  {
    method: "get",
    path: "/:id/event/last-communication",
    alias: "findSiteLastCommunication",
    response: eventDtoSchema.nullable(),
  },
  {
    method: "get",
    path: "/:id/mombox",
    alias: "findSiteMombox",
    response: z.array(momboxDtoSchema),
  },
  {
    method: "get",
    path: "/:id/event/last-hardware-configuration",
    alias: "findSiteLastHardwareConfiguration",
    response: eventDtoSchema.nullable(),
  },
  {
    method: "get",
    path: "/:id/modulators",
    alias: "findSiteModulators",
    response: z.array(modulatorDtoSchema),
  },
  {
    method: "post",
    path: "/:id/supportMail",
    alias: "sendSupportMail",
    response: z.any(),
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: sendSupportMailBody,
      },
    ],
  },
  {
    method: "post",
    path: "/:id/rotationMail",
    alias: "sendRotationMail",
    response: z.any(),
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: sendRotationMailBody,
      },
    ],
  },
  {
    method: "put",
    path: "/:id/attach-operation",
    alias: "attachOperation",
    response: z.unknown(),
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: z.object({
          missionId: z.number(),
          operationId: z.number(),
        }),
      },
    ],
  },
  {
    method: "get",
    path: "/:id/mombox-last-connection-date",
    alias: "findSiteMomboxLastConnectionDate",
    response: z.array(momboxLastConnectionDateDtoSchema),
  },
  {
    method: "get",
    path: "/:id/sensor/request",
    alias: "findSiteSensorRequests",
    response: z.array(sensorRequestDtoSchema),
  },
  {
    method: "post",
    path: "/:id/sensor/request",
    alias: "sendSiteSensorRequest",
    response: z.any(),
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: z.object({
          amount: z.number().min(1).max(10),
          comment: z.string().min(1),
          csLinkIds: z.array(z.number()).min(1),
        }),
      },
    ],
  },
  {
    method: "post",
    path: "/:id/sendAgreementLetter",
    alias: "sendAgreementLetter",
    response: z.any(),
  },
  {
    method: "get",
    path: "/:id/myVDirectAccess",
    alias: "findMyVDirectAccessUrl",
    response: z.object({ url: z.string().url() }),
  },
  {
    method: "post",
    path: "/:id/events/phoningDirectory/create",
    parameters: [
      {
        type: "Path",
        name: "id",
        schema: branded.siteId,
      },
    ],
    alias: "openSitePhoningDirectory",
    response: z.any(),
  },
  {
    method: "get",
    path: "/:id/service-providers",
    alias: "findSiteAvailableServiceProviders",
    response: z.array(serviceProviderDtoSchema),
  },
  {
    method: "post",
    path: "/:id/appointments/partner/availabilities",
    alias: "findAppointmentAvailabilities",
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: z.object({
          appointmentType: z.nativeEnum(EAppointmentType),
          availabilityPreference: z.null(),
          endDate: branded.localeDateTime,
          startDate: branded.localeDateTime,
          serviceProvidersId: z.array(z.number()),
        }),
      },
    ],
    response: appointmentSearchDtoSchema,
  },
  {
    method: "post",
    path: "/:siteId/appointments/partner/fixed",
    alias: "bookManualForcedAppointment",
    response: z.object({ id: z.number() }),
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: z.object({
          appointmentType: z.nativeEnum(EAppointmentType),
          comment: z.string(),
          endDate: branded.localeDateTime,
          startDate: branded.localeDateTime,
          serviceProviderId: z.number(),
          slotId: z.null(),
          reason: z.string(),
        }),
      },
    ],
  },
  {
    method: "post",
    path: "/:id/appointments/partner",
    alias: "bookAppointment",
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: z.object({
          appointmentType: z.nativeEnum(EAppointmentType),
          comment: z.string(),

          endDate: z.string(), // TODO: Map this to correct object / type, as we send LocalDateTime, we cannot cast is as date as it will consider the given date as zoned date. Hence, this cause this endpoint to consider zoned date time instead of local date time

          startDate: z.string(), // TODO: Map this to correct object / type, as we send LocalDateTime, we cannot cast is as date as it will consider the given date as zoned date. Hence, this cause this endpoint to consider zoned date time instead of local date time
          serviceProviderId: z.number(),
          slotId: z.any(),
          slotState: z.nativeEnum(EAppointmentSlotState),
          appointmentCancellationReasonId: z.number().nullable().default(null),
          cancellationComment: z.string().nullable().default(null),
          reason: z.string().nullable().default(null),
        }),
      },
    ],
    response: z.object({ id: z.number() }),
  },
  {
    method: "put",
    path: "/:id/appointments/flexible",
    alias: "bookFlexAppointment",
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: z.object({
          appointmentType: z.nativeEnum(EAppointmentType),
          comment: z.string(),
          appointmentCancellationReasonId: z.number().nullable().default(null),
          cancellationComment: z.string().nullable().default(null),
          siteId: z.number(),
        }),
      },
    ],
    response: z.object({ id: z.number() }),
  },
  {
    method: "get",
    path: "/:id/appointments",
    alias: "findSiteLastAppointments",
    parameters: [
      {
        type: "Query",
        name: "from",
        schema: z.string(),
      },
    ],
    response: z.array(appointmentDtoSchema),
  },
  {
    method: "get",
    path: "/appointments/reason/:reasonTypeId",
    alias: "findAppointmentCancellationReasonsById",
    response: z.array(appointmentCancellationReasonDtoSchema),
  },
  {
    method: "post",
    path: "/:id/appointments/:appId/cancel",
    alias: "cancelSiteAppointment",
    response: z.any(),
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: z.object({
          appointmentCancellationReasonId: z.number(),
          comments: z.string().min(1),
        }),
      },
    ],
  },
  {
    method: "get",
    path: "/:siteId/appointments/preparation",
    alias: "findAppointmentPreparationBySiteId",
    parameters: [
      {
        type: "Path",
        name: "siteId",
        schema: z.number(),
      },
    ],
    response: siteAppointmentPreparationDtoSchema.required(),
  },
  {
    method: "get",
    path: "/:siteId/events/tree",
    alias: "findEventsBySiteId",
    parameters: [
      {
        type: "Path",
        name: "siteId",
        schema: z.number(),
      },
    ],
    response: z.array(eventTreeSchema),
  },
  {
    method: "post",
    path: "/:siteId/events/phoningDirectory/close",
    parameters: [
      {
        type: "Path",
        name: "siteId",
        schema: z.number(),
      },
      {
        type: "Body",
        name: "body",
        schema: z.object({
          callReasonId: z.number(),
          callTypeId: z.number().nullable(),
          comment: z.string().min(0).nullable(),
        }),
      },
    ],
    response: z.any(),
    alias: "closePhoningDirectory",
  },
  {
    method: "put",
    path: "/:siteId/subscriber/:subscriberId/attach",
    alias: "attachSubscriberToSite",
    response: z.any(),
  },
  {
    method: "get",
    path: "/:siteId/installation-flow",
    alias: "findSiteInstallationFlow",
    parameters: [
      {
        type: "Path",
        name: "siteId",
        schema: z.number(),
      },
    ],
    response: siteInstallationFlowDtoSchema,
  },
  {
    method: "get",
    path: "/:siteId/installation-status",
    alias: "findSiteInstallationStatus",
    parameters: [
      {
        type: "Path",
        name: "siteId",
        schema: z.number(),
      },
    ],
    response: siteInstallationStatusDtoSchema,
  },
  {
    method: "post",
    path: "/:siteId/appointments/manual",
    alias: "bookManualAppointment",
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: appointmentManualBookingDtoSchema,
      },
      {
        type: "Path",
        name: "siteId",
        schema: z.number(),
      },
    ],
    response: z.object({ id: z.number() }),
  },
  {
    method: "post",
    path: "/:siteId/photos",
    alias: "uploadSitePhoto",
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: z.any(),
      },
    ],
    response: z.any(),
  },
  {
    method: "get",
    path: "/:siteId/first-service-provider",
    alias: "findSiteFirstServiceProvider",
    response: serviceProviderDtoSchema.nullish(),
  },
  {
    method: "get",
    path: "/:id/documents",
    alias: "findSiteDocuments",
    response: siteDocumentsDtoSchema,
  },
  {
    method: "get",
    path: "/:id/documents-with-photos",
    alias: "findSiteDocumentsWithPhotos",
    response: siteDocumentsWithPhotosDtoSchema,
  },
  {
    method: "post",
    path: "/:siteId/subscriber-offer",
    alias: "updateSubscriberOffer",
    parameters: [
      {
        type: "Query",
        name: "isPremium",
        schema: z.boolean(),
      },
    ],
    response: z.string(),
  },
  {
    method: "get",
    path: "/:siteId/modulator/:csId/consumption",
    alias: "findModulatorPowerHistory",
    parameters: [
      {
        name: "siteId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "csId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "from",
        type: "Query",
        schema: z.string().regex(DATE_REGEX),
      },
      {
        name: "to",
        type: "Query",
        schema: z.string().regex(DATE_REGEX),
      },
    ],
    response: modulatorPowerMeasureHistoryDto,
  },
  {
    method: "patch",
    path: "/:siteId/documents/:documentId/metadata",
    alias: "updateSiteDocumentMetadata",
    parameters: [
      {
        name: "siteId",
        type: "Path",
        schema: branded.siteId,
      },
      {
        name: "documentId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "body",
        type: "Body",
        schema: patchSiteDocumentMetadataDtoSchema,
      },
    ],
    response: z.unknown(),
  },
]);
